import {
  createConnectQueryKey,
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from "@connectrpc/connect-query";

import { SourceStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  GetPresignedDownloadUriRequest,
  GetPresignedDownloadUriResponse,
  GetSourceRequest,
  GetSourceResponse,
  GetSourceUsageRequest,
  GetSourceUsageResponse,
  ListSourcesRequest,
  ListSourcesRequestSchema,
  ListSourcesResponse,
} from "@gx/ts-contracts/services/source/api/source_pb";
import {
  getPresignedDownloadUri,
  getSource,
  getSourceUsage,
  listSources,
} from "@gx/ts-contracts/services/source/api/source-SourceService_connectquery";
import { PaginationRequestSchema } from "@gx/ts-contracts/shared/pagination_pb";
import { SortingRequestSchema } from "@gx/ts-contracts/shared/sorting_pb";

import {
  DEFAULT_PAGINATION_CURSOR,
  DEFAULT_PAGINATION_TOTAL,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
} from "@/api/constants";
import { createInfiniteQueryInput, UseInfiniteQueryOptions } from "@/api/types";

// ========== LIST SOURCES ==========

export const createListSourcesQueryKey = (input?: ListSourcesRequest) => {
  return createConnectQueryKey({
    schema: listSources,
    input,
    cardinality: "finite",
  });
};

export const useListSourcesQuery = ({
  input,
  options = {},
}: {
  input?: ListSourcesRequest;
  options?: UseQueryOptions<typeof listSources.output, ListSourcesResponse>;
}) => {
  return useQuery<typeof listSources.input, typeof listSources.output>(listSources, input, options);
};

// ========== LIST SOURCES INFINITE ==========

export const createListSourcesInfiniteQueryKey = (input?: ListSourcesRequest) => {
  return createConnectQueryKey({
    schema: listSources,
    input,
    cardinality: "infinite",
  });
};

export const useListSourcesInfiniteQuery = ({
  input,
  options = {},
}: {
  input?: ListSourcesRequest;
  options?: UseInfiniteQueryOptions<
    typeof listSources.input,
    typeof listSources.output,
    "pagination"
  >;
}) => {
  const inputTotal = input?.pagination?.total ?? DEFAULT_PAGINATION_TOTAL;
  const inputCursor = input?.pagination?.cursor ?? DEFAULT_PAGINATION_CURSOR;
  const inputSortBy = input?.sorting?.sortBy ?? DEFAULT_SORT_BY;
  const inputSortOrder = input?.sorting?.sortOrder ?? DEFAULT_SORT_ORDER;

  const queryInput = createInfiniteQueryInput(ListSourcesRequestSchema, {
    pagination: createInfiniteQueryInput(PaginationRequestSchema, {
      total: inputTotal,
      cursor: inputCursor,
    }),
    sorting: createInfiniteQueryInput(SortingRequestSchema, {
      sortBy: inputSortBy,
      sortOrder: inputSortOrder,
    }),
  });
  if (input?.filter != null) {
    queryInput.filter = input.filter;
  }

  return useInfiniteQuery<typeof listSources.input, typeof listSources.output, "pagination">(
    listSources,
    queryInput,
    {
      pageParamKey: "pagination",
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination?.nextCursor) return undefined;
        return createInfiniteQueryInput(PaginationRequestSchema, {
          total: inputTotal,
          cursor: lastPage.pagination.nextCursor,
        });
      },
      ...options,
    },
  );
};

// ========== GET SOURCE ==========
export const createGetSourceQueryKey = (input: GetSourceRequest) => {
  return createConnectQueryKey({
    schema: getSource,
    input,
    cardinality: "finite",
  });
};

export const useGetSourceQuery = ({
  input,
  options = {},
}: {
  input: GetSourceRequest;
  options?: UseQueryOptions<typeof getSource.output, GetSourceResponse>;
}) => {
  return useQuery<typeof getSource.input, typeof getSource.output>(getSource, input, {
    refetchInterval: (query) => {
      const status = query.state.data?.source?.sourceStatus;
      // Note: There should not be a need to refetch source faster than a minute.
      // To get details of syncs, use the source syncs query instead.
      return status === SourceStatus.READY ? false : 60 * 1000;
    },
    ...options,
  });
};

export const useGetPresignedDownloadUriQuery = ({
  input,
  options = {},
}: {
  input: GetPresignedDownloadUriRequest;
  options?: UseQueryOptions<typeof getPresignedDownloadUri.output, GetPresignedDownloadUriResponse>;
}) => {
  return useQuery<typeof getPresignedDownloadUri.input, typeof getPresignedDownloadUri.output>(
    getPresignedDownloadUri,
    input,
    options,
  );
};

// ========== GET SOURCE USAGE ==========
export const createGetSourceUsageQueryKey = (input: GetSourceUsageRequest) => {
  return createConnectQueryKey({
    schema: getSourceUsage,
    input,
    cardinality: "finite",
  });
};

export const useGetSourceUsageQuery = ({
  input,
  options = {},
}: {
  input: GetSourceUsageRequest;
  options?: UseQueryOptions<typeof getSourceUsage.output, GetSourceUsageResponse>;
}) => {
  return useQuery<typeof getSourceUsage.input, typeof getSourceUsage.output>(
    getSourceUsage,
    input,
    options,
  );
};
